import { useCallback, useMemo } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowDown01Icon } from '@hugeicons/core-stroke-rounded'
import { Trans } from '@lingui/react/macro'

import Checkbox from './Checkbox'

export type ValueType = string | number

interface Option<T extends ValueType> {
  label: string
  value: T
}

interface Props<T extends ValueType> {
  options: Option<T>[]
  selectedValues: T[]
  placeholder: string
  onChange: (values: T[]) => void
}

export default function FilterSelect<T extends ValueType>({
  options,
  selectedValues,
  placeholder,
  onChange
}: Props<T>) {
  const onToggleValue = useCallback(
    (checked: boolean, value: T) => {
      const newValue = checked
        ? [...selectedValues, value]
        : selectedValues.filter(v => v !== value)

      const sortedValue = options.flatMap(function (option) {
        if (newValue.includes(option.value)) return option.value
        return []
      })

      onChange(sortedValue)
    },
    [selectedValues, options, onChange]
  )

  const onClear = useCallback(() => {
    onChange([])
  }, [onChange])

  const selectedLabels = useMemo(() => {
    return selectedValues.map(
      value => options.find(option => option.value === value)?.label
    )
  }, [selectedValues, options])

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="flex select-none flex-row items-center gap-1 rounded-small border border-text bg-white py-1.5 pl-2 pr-3">
          <HugeiconsIcon icon={ArrowDown01Icon} className="size-4" />
          {selectedValues.length > 0 ? selectedLabels.join(', ') : placeholder}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 flex flex-col gap-1 rounded-small bg-white p-3 shadow-md outline-hidden z-popover"
        sideOffset={5}
      >
        {options.map(function (option) {
          const isSelected = selectedValues.includes(option.value)

          return (
            <DropdownMenu.CheckboxItem
              className="flex cursor-pointer flex-row items-center gap-2 rounded-small p-2 focus:bg-primary-bright focus:outline-hidden"
              key={option.value}
              checked={isSelected}
              onCheckedChange={checked => onToggleValue(checked, option.value)}
              onSelect={event => event.preventDefault()}
            >
              <div className="pointer-events-none">
                <Checkbox checked={isSelected} readOnly>
                  {option.label}
                </Checkbox>
              </div>
            </DropdownMenu.CheckboxItem>
          )
        })}
        {selectedValues.length > 0 && (
          <DropdownMenu.Item
            className="flex cursor-pointer flex-row items-center justify-center gap-2 p-2 text-red-500"
            onClick={onClear}
          >
            <Trans>Clear Filter</Trans>
          </DropdownMenu.Item>
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}
